import type { ContextValues } from "@atoms/atom-types";

export type ApiOperationTargetKind = "entity" | "atom" | "reducer";
export type ApiOperationKind =
  | "create"
  | "delete"
  | "exists"
  | "update"
  | "set"
  | "addList"
  | "removeList"
  | "customReducer";
export type LocalizedErrorText = Readonly<Record<string, string>>;

export type ApiOperationErrorContext = Readonly<{
  operation: ApiOperationKind;
  targetKind: ApiOperationTargetKind;
  /**
   * Short noun-phrase label used by entity/atom operations to template
   * success/failure phrases (e.g. `${label} erfolgreich erstellt.`).
   * Not used for reducer operations.
   */
  label?: LocalizedErrorText;
  /**
   * Explicit success message used by reducer operations (entities/atoms
   * derive their success phrasing from `label`).
   */
  successMessage?: LocalizedErrorText;
  /**
   * Explicit failure message override; takes precedence over the
   * label-templated failure phrase.
   */
  failureMessage?: LocalizedErrorText;
}>;

export type ApiRequestOptions = Readonly<{
  showErrorToast?: boolean;
  showSuccessToast?: boolean;
  extraContext?: ContextValues;
}>;

export type ApiRequestOptionsOrContext =
  | ApiRequestOptions
  | ContextValues
  | undefined;

export type DeleteEntityOptions = Readonly<{
  recursive?: boolean;
}>;

export type ApiErrorHandlingOptions = Readonly<{
  showErrorToast?: boolean;
  defaultMessage?: string;
  keyPrefix?: string;
  operationContext?: ApiOperationErrorContext;
}>;

export type ApiSuccessHandlingOptions = Readonly<{
  showSuccessToast?: boolean;
  successMessage?: string;
  keyPrefix?: string;
  operationContext?: ApiOperationErrorContext;
}>;

export const MANUAL_API_REQUEST_OPTIONS = Object.freeze({
  showErrorToast: false,
  showSuccessToast: false,
}) satisfies ApiRequestOptions;

const APOLLO_API_ERROR_HANDLING_CONTEXT_KEY = "__apiErrorHandling";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isApiRequestOptions(value: unknown): value is ApiRequestOptions {
  return (
    isRecord(value) &&
    ("showErrorToast" in value ||
      "showSuccessToast" in value ||
      "extraContext" in value)
  );
}

export function normalizeApiRequestOptions(
  requestOptions?: ApiRequestOptionsOrContext,
): {
  showErrorToast: boolean;
  showSuccessToast: boolean;
  extraContext?: ContextValues;
} {
  if (!requestOptions) {
    return { showErrorToast: true, showSuccessToast: false };
  }

  if (isApiRequestOptions(requestOptions)) {
    return {
      showErrorToast: requestOptions.showErrorToast ?? true,
      showSuccessToast: requestOptions.showSuccessToast ?? false,
      extraContext: requestOptions.extraContext,
    };
  }

  return {
    showErrorToast: true,
    showSuccessToast: false,
    extraContext: requestOptions,
  };
}

export function mergeApiRequestContext(
  baseContext: ContextValues,
  requestOptions?: ApiRequestOptionsOrContext,
): ContextValues {
  const normalized = normalizeApiRequestOptions(requestOptions);
  return normalized.extraContext
    ? {
        ...baseContext,
        ...normalized.extraContext,
      }
    : baseContext;
}

export function createApiErrorHandlingOptions(
  defaults: Omit<ApiErrorHandlingOptions, "showErrorToast">,
  requestOptions?: ApiRequestOptionsOrContext,
): ApiErrorHandlingOptions {
  const normalized = normalizeApiRequestOptions(requestOptions);
  return {
    ...defaults,
    showErrorToast: normalized.showErrorToast,
  };
}

export function createApiSuccessHandlingOptions(
  defaults: Omit<ApiSuccessHandlingOptions, "showSuccessToast">,
  requestOptions?: ApiRequestOptionsOrContext,
): ApiSuccessHandlingOptions {
  const normalized = normalizeApiRequestOptions(requestOptions);
  return {
    ...defaults,
    showSuccessToast: normalized.showSuccessToast,
  };
}

export function buildApolloApiErrorHandlingContext(
  options: ApiErrorHandlingOptions,
): Record<string, ApiErrorHandlingOptions> {
  return {
    [APOLLO_API_ERROR_HANDLING_CONTEXT_KEY]: options,
  };
}

export function getApiErrorHandlingOptionsFromApolloContext(
  context: unknown,
): ApiErrorHandlingOptions | undefined {
  if (!isRecord(context)) {
    return undefined;
  }

  const apiContext = context[APOLLO_API_ERROR_HANDLING_CONTEXT_KEY];
  return isRecord(apiContext)
    ? (apiContext as ApiErrorHandlingOptions)
    : undefined;
}
